import { computed, inject } from 'vue';

import { ProvidedTrayState } from '@/injectionKeys/integration_profiles';
import { useFeedsStore, useSourcesStore } from '@/stores';

const SUPPORTED_SALESFORCE_FIELD_TYPE = 'multipicklist';

export type SalesforceCustomFieldOption = {
  label: string;
  value: string;
  syncing_disabled_reason: string | null | undefined;
  isSupported: boolean;
};

export type SelectOption = { label: string; value: string };

/**
 * Why a saved mapping cannot be used, or null when it is still valid.
 * Nothing mapped yet, or fields not loaded yet, is not an error.
 */
export function getFieldErrorMessage(
  crmField: string | undefined,
  fields: SalesforceCustomFieldOption[],
): string | null {
  if (!crmField || !fields.length) return null;
  const field = fields.find((f) => f.value === crmField);
  if (!field || field.syncing_disabled_reason)
    return 'Field cannot be found in Salesforce';
  if (!field.isSupported)
    return 'Only multi-select picklist fields are supported';
  return null;
}

/**
 * Shared logic for Salesforce Account custom fields used by tray push mappings.
 * Tracks which fields are used by any mapping (ecosystem + partner tags) and which are still available.
 */
export function useSalesforcePicklistFieldsForPush() {
  const trayState = inject(ProvidedTrayState);

  const feedsStore = useFeedsStore();
  const salesforceFeed = computed(() =>
    feedsStore.crmFeeds.find((feed) => feed.integration.type === 'salesforce'),
  );
  const feedId = computed(() => salesforceFeed.value?.id);

  const { getSourcesByMdmType } = useSourcesStore();
  const sources = computed(() =>
    getSourcesByMdmType('account').filter(
      (source) => source.feed_id === feedId.value,
    ),
  );

  const allCustomFields = computed((): SalesforceCustomFieldOption[] =>
    (sources.value[0]?.fields ?? [])
      .filter((field) => field.column.endsWith('__c'))
      .map((field) => ({
        label: field.nickname ?? field.column,
        value: field.column,
        syncing_disabled_reason: field.syncing_disabled_reason,
        isSupported:
          field.original_data_type === SUPPORTED_SALESFORCE_FIELD_TYPE,
      })),
  );

  const usedCrmFieldValues = computed(() => {
    if (!trayState) return [];
    const used: string[] = [];
    trayState.ecosystemMaps?.forEach((m) => {
      if (m.crm_field) used.push(m.crm_field);
    });
    trayState.partnerAttributes.forEach((m) => {
      if (m.crm_field) used.push(m.crm_field);
    });
    return used;
  });

  const availableFieldOptions = computed((): SelectOption[] =>
    allCustomFields.value
      .filter(
        (field) =>
          field.isSupported &&
          !field.syncing_disabled_reason &&
          !usedCrmFieldValues.value.includes(field.value),
      )
      .map((field) => ({ label: field.label, value: field.value })),
  );

  /**
   * Options for a single mapping select: available fields plus the current
   * selection so the dropdown always shows what is mapped, including a field
   * that is unsupported or gone from Salesforce. Without it the select renders
   * empty next to its error and the user cannot tell which field is at fault.
   */
  function getOptionsForSelect(selectedValue?: string): SelectOption[] {
    const available = availableFieldOptions.value;
    if (!selectedValue) return available;
    if (available.some((o) => o.value === selectedValue)) return available;
    const selectedField = allCustomFields.value.find(
      (f) => f.value === selectedValue,
    );
    return [
      {
        label: selectedField?.label ?? selectedValue,
        value: selectedValue,
      },
      ...available,
    ];
  }

  return {
    allCustomFields,
    usedCrmFieldValues,
    availableFieldOptions,
    getOptionsForSelect,
  };
}
